import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { get, uniqueId } from 'lodash';
import axios from 'axios';
import {
	SdfAlert,
	SdfBusyIndicator,
	SdfButton,
	SdfIcon,
	SdfWizardLayout,
	SdfWizardStepsVertical,
	SdfWizardStepsVerticalItem
} from '@synerg/react-components';
import { PartnerTypeAndPlatform } from '../../common/partnerTypeAndPlatform';
import StepOne from './stepsDC/stepOne';
import StepTwo from './stepsDC/stepTwo';
import StepThree from './stepsDC/stepThree';

const defaultValues = {
	projectName: '',
	projectDescription: '',
	companySize: 'generic_datacloud',
	useCaseID: null,
	useCaseName: null,
	appStore: 'US',
	platform: PartnerTypeAndPlatform.datacloud.toLowerCase(),
	productIntegration: 'ADP® DataCloud'
};

export default function ProjectStepsDC({hideModal}) {
	const [useCases, setUseCases] = useState([]);
	const [, setProducts] = useState([]);

	const [loading, setLoading] = useState(false);

	const [applicationNameList, setApplicationNameList] = useState(undefined);
	const [error, setError] = useState(false);
	const [errorMessage, setErrorMessage] = useState('');

	const { control, formState, getValues, setValue } = useForm({ mode: 'onChange', defaultValues });

	const [activeStep, setActiveStep] = useState(0);
	const [validForm, setValidForm] = useState(true);

	const [selectedUseCaseBtnIndex, setSelectedUseCaseBtnIndex] = useState(null);

	useEffect(() => {
		async function getUseCases() {
			setLoading(true);
			try {
				Promise.all([
					axios(`${import.meta.env.VITE_ROOT_PATH}/api/organization/applications/name`),
					axios(`${import.meta.env.VITE_ROOT_PATH}/api/project/use-cases`),
					axios(`${import.meta.env.VITE_ROOT_PATH}/api/products`,
							{params: {includeDataCloud: true}})]).then(([
					{data: appNameList, status: appNameStatus}, {
						data: useCases,
						status: useCasesStatus,
					}, {data: products, status: productsStatus}]) => {
					appNameStatus === 200
							? setApplicationNameList(appNameList)
							: setErrorMessage(
									'Something went wrong when trying to get application name list. Please try again.');
					useCasesStatus === 200 ? setUseCases(useCases) : setErrorMessage(
							'Something went wrong when trying to get use-cases. Please try again.');
					productsStatus === 200 ? setProducts(products) : setErrorMessage(
							'Something went wrong when trying to get product list. Please try again.');
					if (appNameStatus !== 200 || useCasesStatus !== 200 ||
							productsStatus !== 200) {
						setError(true);
					}
					setLoading(false);
				}).catch((e) => {
					setLoading(false);
					setErrorMessage(e.message);
					setError(true);
				});
			} catch (e) {
				setLoading(false);
				setError(true);
			}
		}

		getUseCases();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		if (formState.isValid) {
			setValidForm(true);
		} else {
			setValidForm(false);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [formState.isValid]);

	const backClickHandler = () => {
		if (activeStep !== 0) {
			setActiveStep(activeStep - 1);
		}
	};

	const nextClickHandler = async() => {
		if (activeStep < 2) {
			setActiveStep(activeStep + 1);
		} else {
			await createProject();
		}
	};

	const createProject = async () => {
		setLoading(true);
		try {
			const dataToSend = {
				appStore: getValues().appStore,
				companySize: getValues().companySize,
				platform: getValues().platform,
				projectDescription: getValues().projectDescription,
				projectName: getValues().projectName,
				useCaseID: getValues().useCaseID
			}

			const { data } = await axios.post(`${import.meta.env.VITE_ROOT_PATH}/api/project`, dataToSend);
			if (data._id) {
				hideModal(false);
			} else {
				setError(true);
				setErrorMessage('Unable to create project. Please try again');
			}
		} catch (e) {
			const errorMessage = get(e, 'response.data.message', 'Something went wrong when trying to create project. Please try again.');
			setErrorMessage(errorMessage);
			setError(true);
		}
		setLoading(false);
	}

	function canGoNext() {
		switch (activeStep) {
		case 0:
			return validForm;
		case 1:
			if (selectedUseCaseBtnIndex !== null) {
				return true;
			}
			break;
		case 2:
			return true;
		default:
			return false;
		}
	}

	const ShowError = ({message}) => {
		return (
			<SdfAlert key={uniqueId()} status={'error'} showToast={true}>
				<span slot="title"> Error </span>
				<div>{message || 'Something went wrong, please try again later'}</div>
			</SdfAlert>
		);
	};

	return (<div>
		{loading && <SdfBusyIndicator style={{color: '#1a8099'}} size="xl" fullScreen={true} />}
		<SdfWizardLayout>
			{error && <div slot="page-content" className="px-8 mb-2 min-w-fit">
				<ShowError message={errorMessage}/>
			</div>}
			<div slot="page-content" className="px-8 hxl overflow-y-scroll">
				{activeStep === 0 && <StepOne state={getValues()} control={control} setError={setError}
												applicationNameList={applicationNameList} loadingAppNames={loading} />}
				{activeStep === 1 && <StepTwo state={getValues()} useCases={useCases}
												selectedUseCaseBtnIndex={selectedUseCaseBtnIndex}
												setSelectedUseCaseBtnIndex={setSelectedUseCaseBtnIndex} setValue={setValue} />}
				{activeStep === 2 && <StepThree state={getValues()}/>}
			</div>

			<SdfWizardStepsVertical slot="wizard-steps" className="flex-grow-0 w-64" style={{marginTop: '30px'}}>
				<SdfWizardStepsVerticalItem className="w-48" stepStatus="visited"	isCurrent={activeStep === 0}>
					Project	details
				</SdfWizardStepsVerticalItem>
				<SdfWizardStepsVerticalItem className="w-64" isCurrent={activeStep === 1} stepStatus={activeStep > 1 ? 'visited' : 'unvisited'}>
					Use case selection
				</SdfWizardStepsVerticalItem>
				<SdfWizardStepsVerticalItem className="w-64" stepStatus={activeStep > 2 ? 'visited' : 'unvisited'} isCurrent={activeStep === 2}>
					Review
				</SdfWizardStepsVerticalItem>
			</SdfWizardStepsVertical>

			<SdfButton slot="buttons" emphasis="tertiary" variant="contained" onClick={() => {hideModal(false);}}> Cancel </SdfButton>
			{activeStep > 0 && <SdfButton slot="buttons" variant="contained" onClick={backClickHandler}>
				<SdfIcon icon="nav-move-left" /> Back
			</SdfButton>}
			<SdfButton disabled={!canGoNext() ? 'true' : undefined} slot="buttons" emphasis="primary" iconPlacement="after"
					variant="contained" onClick={nextClickHandler}>
				{activeStep === 2 ? 'Generate' : 'Next'}
				{activeStep !== 2 && <SdfIcon icon="nav-move-right" />}
			</SdfButton>

		</SdfWizardLayout>
	</div>);
}
